import { useMemo } from 'react';
import { Alert, Combobox, type ComboboxOption } from '@billda/design-system';
import { useAllLabourDefinitions } from '../../../labour/hooks/useLabourQueries.ts';
import type { LabourItemDefinitionSummary } from '../../../labour/types/labourItemTypes.ts';
import type { FC } from 'react';

interface LabourItemAutocompleteProps {
  value: LabourItemDefinitionSummary | null;
  onChange: (value: LabourItemDefinitionSummary | null) => void;
  error?: string;
}

/**
 * Organism: Autocomplete for labour item selection
 * Features:
 * - Preloaded data for instant filtering
 * - Client-side filtering
 * - Keyboard navigation
 * - Highlight matching text
 */
export const LabourItemAutocomplete: FC<LabourItemAutocompleteProps> = ({
  value,
  onChange,
  error,
}) => {
  const { data, isLoading, isError } = useAllLabourDefinitions();

  const definitions = data?.definitions ?? [];

  const options: ComboboxOption[] = useMemo(
    () =>
      definitions.map(def => ({
        label: def.name,
        value: def.id,
      })),
    [definitions]
  );

  const handleChange = (selectedId: string | number | undefined) => {
    if (selectedId === undefined) {
      onChange(null);
      return;
    }
    const selected = definitions.find(def => def.id === selectedId) ?? null;
    onChange(selected);
  };

  return (
    <div>
      <Combobox
        options={options}
        value={value?.id}
        onChange={handleChange}
        label="Select Labour Item *"
        placeholder="Start typing to filter..."
        emptyText={
          options.length === 0
            ? 'Type to filter labour items'
            : 'No matching labour items found'
        }
        loading={isLoading}
        loadingText="Loading labour items…"
        invalid={!!error}
      />
      {error ? (
        <p className="mt-1.5 type-caption text-content-error" role="alert">
          {error}
        </p>
      ) : (
        <p className="mt-1.5 type-caption text-content-secondary">
          Select from existing labour items
        </p>
      )}

      {isError && (
        <Alert severity="error" className="mt-2">
          Failed to load labour items. Please try again.
        </Alert>
      )}
    </div>
  );
};
